/**
 * Immutable decision history + the one permitted review mutation (approve /
 * reject a SUBMITTED revision), rendered by the Decision tab's Decide section
 * (`DecideSurface`). The Path to GO Decide phase that also rendered it was
 * deleted in stage 3.
 *
 * Moved from `pathToGo/DecisionStage.tsx` (Path to GO removal, stage 1). Each
 * row shows the frozen evidence (system recommendation, score, Test Fit
 * authority), the author, the rationale, and any rejection reason.
 *
 * ROLE GATE: review controls render only when the host passes
 * `onReviewDecision` — the host decides who may review (platform admin,
 * company approver, company admin) and the server re-checks it. This component
 * never persists anything itself.
 */
import React from 'react';
import { History } from 'lucide-react';
import { Button } from '../../ui/button';
import { DECISION_BAND_LABEL } from '../../../lib/portfolio/decisionBands';
import type { PathToGoDecisionRecord } from '../../../types/pathToGoDecision';

export function formatDecisionTimestamp(value: string | null | undefined): string {
  if (!value) return 'Not recorded';
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(parsed));
}

function frozenSystemLabel(record: PathToGoDecisionRecord): string {
  const system = record.evidence?.system;
  if (system?.label) return system.label;
  // Canonical labels, never a local copy of the four strings. `withheld` is a
  // system state rather than a band, so it is not in the band label map.
  if (system?.band && system.band !== 'withheld') return DECISION_BAND_LABEL[system.band];
  return 'Withheld';
}

export interface DecisionHistoryListProps {
  /** Immutable history, newest first. Nothing renders when empty. */
  decisionRecords: readonly PathToGoDecisionRecord[];
  /** Review bridge for the only permitted mutation of a submitted revision. */
  onReviewDecision?: (
    recordId: string,
    outcome: 'approved' | 'rejected',
    rejectionReason?: string,
  ) => void | Promise<void>;
  /** Read-only viewer (site locked by someone else): no review controls. */
  decisionReadOnly?: boolean;
}

export const DecisionHistoryList: React.FC<DecisionHistoryListProps> = ({
  decisionRecords,
  onReviewDecision,
  decisionReadOnly = false,
}) => {
  const [rejectingRecordId, setRejectingRecordId] = React.useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [reviewingRecordId, setReviewingRecordId] = React.useState<string | null>(null);
  const [reviewError, setReviewError] = React.useState<{
    recordId: string;
    message: string;
  } | null>(null);

  const submitReview = async (
    recordId: string,
    outcome: 'approved' | 'rejected',
  ) => {
    if (!onReviewDecision || decisionReadOnly || reviewingRecordId) return;
    const reason = rejectionReason.trim();
    if (outcome === 'rejected' && !reason) {
      setReviewError({
        recordId,
        message: 'Add a reason before rejecting this decision.',
      });
      return;
    }
    setReviewingRecordId(recordId);
    setReviewError(null);
    try {
      await onReviewDecision(
        recordId,
        outcome,
        outcome === 'rejected' ? reason : undefined,
      );
      setRejectingRecordId(null);
      setRejectionReason('');
    } catch (error) {
      setReviewError({
        recordId,
        message: (error as Error)?.message ?? 'The decision review could not be saved.',
      });
    } finally {
      setReviewingRecordId(null);
    }
  };

  if (decisionRecords.length === 0) return null;

  return (
    <section className="border-t border-border-subtle pt-4" aria-label="Decision history" data-testid="decision-history">
      <div className="type-meta mb-2 flex items-center gap-1.5 font-bold text-text-secondary">
        <History className="h-3.5 w-3.5" aria-hidden="true" />
        Decision history
      </div>
      <ol className="divide-y divide-border-subtle rounded-lg border border-border bg-background-elevated px-3">
        {decisionRecords.map((record) => (
          <li
            key={record.id}
            className="space-y-2.5 py-3"
            data-testid={`decision-history-row-${record.id}`}
            aria-busy={reviewingRecordId === record.id}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-bold text-text-primary">
                {record.decisionStage === 1 ? 'Stage 1 · Screening · ' : record.decisionStage === 2 ? 'Stage 2 · Final · ' : ''}v{record.version} · {record.decision === 'NO_GO' ? 'NO-GO' : record.decision.replaceAll('_', ' ')}
              </span>
              <span className="type-meta text-text-muted">
                {record.approvalStatus} · {record.authorName}
              </span>
            </div>

            <dl className="grid gap-x-4 gap-y-2 rounded-lg border border-border-subtle bg-background p-2.5 sm:grid-cols-2">
              <div>
                <dt className="type-meta text-text-muted">
                  Decision owner
                </dt>
                <dd
                  className="mt-0.5 text-xs font-bold text-text-primary"
                  data-testid={`decision-owner-${record.id}`}
                >
                  {record.decisionOwner || 'Not assigned'}
                </dd>
              </div>
              <div>
                <dt className="type-meta text-text-muted">
                  Recorded
                </dt>
                <dd
                  className="mt-0.5 text-xs text-text-secondary"
                  data-testid={`decision-timestamp-${record.id}`}
                >
                  <time dateTime={record.submittedAt || record.createdAt}>
                    {formatDecisionTimestamp(record.submittedAt || record.createdAt)}
                  </time>
                </dd>
              </div>
              <div>
                <dt className="type-meta text-text-muted">
                  {record.decisionStage === 1 ? 'Frozen screening source' : 'Frozen system recommendation'}
                </dt>
                <dd
                  className="mt-0.5 text-xs font-bold text-text-primary"
                  data-testid={`decision-system-${record.id}`}
                >
                  {record.decisionStage === 1
                    ? record.evidence.screening?.report
                      ? `Screening Assessment v${record.evidence.screening.report.version}.${record.evidence.screening.report.minorVersion} · SED v${record.evidence.screening.report.sedVersionUsed ?? 'not recorded'}`
                      : 'No eligible screening source was available when recorded.'
                    : frozenSystemLabel(record)}
                  {record.evidence.decisionScope?.kind === 'hypothetical-scenario' && <p className="mt-1 font-normal text-text-secondary">
                    Hypothetical scenario {record.evidence.decisionScope.scenarioId}; this record is not a real-world investment approval.
                  </p>}
                </dd>
              </div>
              <div>
                <dt className="type-meta text-text-muted">
                  {record.decisionStage === 1 ? 'Frozen screening recommendations' : 'Frozen score'}
                </dt>
                <dd
                  className="mt-0.5 text-xs text-text-secondary"
                  data-testid={`decision-score-${record.id}`}
                >
                  {record.decisionStage === 1
                    ? Object.values(record.evidence.screening?.recommendations ?? {}).flatMap(line => line ? [line.value] : []).join(' · ') || 'No architecture recommendation was frozen.'
                    : <>{typeof record.evidence?.system?.composite === 'number'
                      ? `${record.evidence.system.composite.toFixed(2)} composite`
                      : 'Score withheld'}{record.evidence?.system?.confidenceTag ? ` · confidence ${record.evidence.system.confidenceTag}` : ''}</>}
                </dd>
              </div>
            </dl>

            <div>
              <div className="type-meta text-text-muted">
                Rationale
              </div>
              <p
                className="mt-0.5 whitespace-pre-wrap text-xs leading-relaxed text-text-secondary"
                data-testid={`decision-rationale-${record.id}`}
              >
                {record.rationale || 'No rationale recorded.'}
              </p>
            </div>

            {record.evidence?.testFitAuthorityRef && (
              <div
                className="rounded-lg border border-primary/30 bg-primary/5 p-2.5"
                data-testid={`decision-test-fit-authority-${record.id}`}
              >
                <div className="type-meta font-bold text-primary">
                  Frozen Test Fit authority
                </div>
                <div className="mt-1 grid gap-1 text-xs text-text-secondary sm:grid-cols-2">
                  <span>
                    Scenario revision {record.evidence.testFitAuthorityRef.scenarioId}
                    {' · updated '}
                    {formatDecisionTimestamp(
                      record.evidence.testFitAuthorityRef.scenarioUpdatedAt,
                    )}
                  </span>
                  <span>
                    Layout v{record.evidence.testFitAuthorityRef.layoutVersion}
                    {' · '}
                    SED v{record.evidence.testFitAuthorityRef.sedVersion}
                  </span>
                </div>
              </div>
            )}

            {record.rejectionReason && (
              <p
                className="rounded-lg border border-danger/30 bg-danger/5 p-2 text-xs text-text-secondary"
                data-testid={`decision-rejection-reason-${record.id}`}
              >
                Rejection reason: {record.rejectionReason}
              </p>
            )}

            {record.approvalStatus === 'submitted'
              && onReviewDecision
              && !decisionReadOnly && (
              <div
                className="space-y-2"
                data-testid={`decision-review-controls-${record.id}`}
              >
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="mission-primary"
                    size="sm"
                    className="min-h-11"
                    disabled={reviewingRecordId != null}
                    onClick={() => {
                      setRejectingRecordId(null);
                      setRejectionReason('');
                      void submitReview(record.id, 'approved');
                    }}
                    data-testid={`decision-review-approve-${record.id}`}
                  >
                    {reviewingRecordId === record.id
                      ? 'Approving…'
                      : 'Approve'}
                  </Button>
                  <Button
                    type="button"
                    variant="mission-danger"
                    size="sm"
                    className="min-h-11"
                    disabled={reviewingRecordId != null}
                    aria-expanded={rejectingRecordId === record.id}
                    onClick={() => {
                      setRejectingRecordId((current) =>
                        current === record.id ? null : record.id);
                      setRejectionReason('');
                      setReviewError(null);
                    }}
                    data-testid={`decision-review-reject-${record.id}`}
                  >
                    Reject
                  </Button>
                </div>

                {rejectingRecordId === record.id && (
                  <div
                    className="rounded-lg border border-danger/30 bg-danger/5 p-3"
                    data-testid={`decision-review-reject-form-${record.id}`}
                  >
                    <label className="block">
                      <span className="type-meta font-bold text-text-secondary">
                        Rejection reason
                      </span>
                      <input
                        value={rejectionReason}
                        onChange={(event) => setRejectionReason(event.target.value)}
                        disabled={reviewingRecordId != null}
                        autoFocus
                        className="mt-1 min-h-11 w-full rounded-md border border-border bg-background px-3 text-sm text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-danger disabled:opacity-50"
                        data-testid={`decision-review-reason-${record.id}`}
                      />
                    </label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="mission-danger"
                        size="sm"
                        className="min-h-11"
                        disabled={reviewingRecordId != null}
                        onClick={() => void submitReview(record.id, 'rejected')}
                        data-testid={`decision-review-confirm-reject-${record.id}`}
                      >
                        {reviewingRecordId === record.id ? 'Rejecting…' : 'Confirm rejection'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-11"
                        disabled={reviewingRecordId != null}
                        onClick={() => {
                          setRejectingRecordId(null);
                          setRejectionReason('');
                          setReviewError(null);
                        }}
                        data-testid={`decision-review-cancel-reject-${record.id}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {reviewError?.recordId === record.id && (
                  <div
                    role="alert"
                    className="text-xs text-danger"
                    data-testid={`decision-review-error-${record.id}`}
                  >
                    {reviewError.message}
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
};

export default DecisionHistoryList;
